import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSeo } from '../hooks/useSeo';
import { fetchCollection } from '../lib/cms';
import ArticleCard from '../components/ArticleCard';
import VideoFacade from '../components/VideoFacade';
import HelpNotice from '../components/HelpNotice';

export default function ErhvervPage() {
  useSeo({
    title: 'Erhvervsdrivende',
    description: 'Skatunderlup for Erhvervsdrivende: Dybdegående erhvervsjournalistik om moms, skattekontrol, selskabsskat, afgifter og rådgivning for SMV.',
  });

  const [articles, setArticles] = useState([]);
  const [topics, setTopics] = useState([]);
  const [video, setVideo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;
    async function loadErhvervData() {
      try {
        const [allArticles, allTopics, allMedia] = await Promise.all([
          fetchCollection('articles', { sort: 'publish_date', order: 'desc', limit: 20 }),
          fetchCollection('om_skat', { limit: 10 }),
          fetchCollection('presse_film', { limit: 5 }),
        ]);

        if (isMounted) {
          const filtered = (allArticles.items || []).filter(
            (a) => a.audience === 'erhverv' || a.audience === 'begge'
          );
          const filteredTopics = (allTopics.items || []).filter(
            (t) => t.audience === 'erhverv' || t.audience === 'begge'
          );
          const erhvervVideo = (allMedia.items || []).find(
            (m) => (m.audience === 'erhverv' || m.audience === 'begge') && m.media_type === 'film'
          );

          setArticles(filtered);
          setTopics(filteredTopics);
          setVideo(erhvervVideo);
          setLoading(false);
        }
      } catch (err) {
        console.warn('Error loading erhverv data', err);
        if (isMounted) setLoading(false);
      }
    }
    loadErhvervData();
    return () => { isMounted = false; };
  }, []);

  const leadStory = articles[0];
  const otherStories = articles.slice(1);

  return (
    <div className="min-h-screen bg-white">
      {/* Section Header Banner */}
      <div className="border-b border-slate-200 bg-slate-50/70 py-8 px-4">
        <div className="mx-auto max-w-7xl">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[#fd7302]">
            <i className="fa-solid fa-building" aria-hidden="true" />
            <span>Erhvervsredaktionen</span>
          </div>
          <h1 className="mt-1 font-serif text-3xl sm:text-4xl lg:text-5xl font-black text-[#010b27]">
            Skat for Erhvervsdrivende
          </h1>
          <p className="mt-2 max-w-2xl text-sm sm:text-base text-slate-600 leading-relaxed">
            Faglig journalistik om selskabsbeskatning, momsregnskab, revisionskontrol, grønne afgifter og skattemæssige rammevilkår for danske virksomheder.
          </p>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 py-8">
        {loading ? (
          <div className="py-16 text-center font-serif text-slate-500">
            Henter erhvervsartikler fra CMS...
          </div>
        ) : (
          <>
            {/* Top Featured Story */}
            {leadStory && (
              <div className="mb-10 pb-8 border-b border-slate-200">
                <ArticleCard article={leadStory} variant="lead" />
              </div>
            )}

            {/* Grid of latest erhverv articles & Sidebar */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 pb-12 border-b border-slate-200">
              <div className="lg:col-span-8">
                <div className="flex items-center justify-between pb-3 mb-6 border-b border-slate-200">
                  <h2 className="font-serif text-2xl font-bold text-[#010b27]">
                    Seneste artikler for erhverv
                  </h2>
                  <span className="text-xs font-mono text-slate-500">
                    {articles.length} artikler
                  </span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  {articles.map((art) => (
                    <ArticleCard key={art.id} article={art} variant="grid" />
                  ))}
                </div>
              </div>

              {/* Sidebar: Erhverv topics & Help Notice */}
              <aside className="lg:col-span-4 lg:border-l lg:border-slate-200 lg:pl-8 space-y-8">
                <div>
                  <div className="border-b border-slate-200 pb-2 mb-4">
                    <h3 className="font-serif text-lg font-bold text-[#010b27] uppercase tracking-wider">
                      Vidensunivers: Erhverv
                    </h3>
                  </div>
                  <div className="space-y-3">
                    {topics.map((t) => (
                      <Link
                        key={t.id}
                        to={`/om-skat/${t.slug?._ || t.slug || ''}`}
                        className="group block rounded-md border border-slate-200 bg-white p-3.5 hover:border-[#fd7302] transition-all shadow-xs"
                      >
                        <div className="flex items-center justify-between text-[10px] uppercase font-bold text-[#fd7302] mb-1">
                          <span>{t.topic}</span>
                          <i className="fa-solid fa-arrow-right text-[9px] group-hover:translate-x-1 transition-transform" aria-hidden="true" />
                        </div>
                        <h4 className="font-serif text-sm font-bold text-[#010b27] group-hover:text-[#fd7302] transition-colors leading-snug">
                          {t.title}
                        </h4>
                        <p className="mt-1 text-xs text-slate-500 line-clamp-2">
                          {t.summary}
                        </p>
                      </Link>
                    ))}
                  </div>
                </div>

                {/* Editorial Help Box */}
                <HelpNotice variant="sidebar" />
              </aside>
            </div>

            {/* Video Section for Erhverv */}
            {video && (
              <section className="py-10 border-b border-slate-200">
                <div className="mb-6 pb-2 border-b border-slate-200">
                  <span className="text-xs font-bold uppercase tracking-widest text-[#fd7302]">
                    Video & Webinar for Erhverv
                  </span>
                  <h2 className="font-serif text-2xl sm:text-3xl font-bold text-[#010b27]">
                    {video.title}
                  </h2>
                </div>
                <div className="max-w-3xl mx-auto">
                  <VideoFacade
                    videoUrl={video.video_url}
                    coverImage={video.cover_image}
                    title={video.title}
                    duration={video.duration}
                  />
                  <p className="mt-4 text-sm text-slate-600 leading-relaxed italic text-center">
                    {video.description}
                  </p>
                </div>
              </section>
            )}

            {/* Bottom Banner */}
            <div className="pt-8">
              <HelpNotice variant="banner" />
            </div>
          </>
        )}
      </div>
    </div>
  );
}
